// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useRef, useState, type KeyboardEvent, type PointerEvent } from "react";
import { fromPercent, toPercent, type Cents } from "@almanac/domain";
import { useI18n } from "../lib/i18n.js";
import { parseMoney, parsePercent } from "../lib/parse.js";

export interface SplitSliderProps {
  id: string;
  name: string;
  budget: Cents;
  actual: Cents;
  /** Event costs still expected, counted against the budget until paid. */
  scheduled?: Cents;
  pool: Cents;
  /** The amount at the right-hand edge of the track. Held steady while dragging. */
  scale: Cents;
  locked: boolean;
  /** Shown in dollars or as a percentage of the pool; a lock holds whichever is shown. */
  units: "dollars" | "percent";
  onChange: (amount: Cents) => void;
  onToggleLock: () => void;
  /** Switches this line between dollars and percent. Without it there is no switch. */
  onUnits?: (units: "dollars" | "percent") => void;
  /** Held by something else (large purchases): the lock and amount cannot be changed here. */
  fixed?: boolean;
  /** A chevron after the name that opens what is under this line. */
  expand?: { open: boolean; count: number; controls: string; label: string; onToggle: () => void };
}

const STEP = 100; // $1
const BIG_STEP = 5_000; // $50

/**
 * A budget slider split in two: the top half is the budget (draggable), the
 * bottom half is what has actually been spent, in the complementary colour.
 * It is one focusable slider; the actual amount is part of its spoken value.
 */
export function SplitSlider(props: SplitSliderProps) {
  const { t, money, percent } = useI18n();
  const labelId = useId();
  const footId = useId();
  const track = useRef<HTMLDivElement>(null);
  const share = toPercent(props.budget, props.pool);
  const shown = props.units === "dollars" ? money(props.budget) : percent(share, 2);
  const width = (v: Cents) => `${Math.min(100, (v / Math.max(1, props.scale)) * 100)}%`;
  const scheduled = props.scheduled ?? 0;
  const remaining = props.budget - props.actual - scheduled;

  const fromPointer = (event: PointerEvent<HTMLDivElement>) => {
    const box = track.current!.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (event.clientX - box.left) / box.width));
    props.onChange(Math.round((ratio * props.scale) / STEP) * STEP);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? STEP * 10 : STEP;
    const moves: Record<string, () => Cents> = {
      ArrowRight: () => props.budget + step,
      ArrowUp: () => props.budget + step,
      ArrowLeft: () => props.budget - step,
      ArrowDown: () => props.budget - step,
      PageUp: () => props.budget + BIG_STEP,
      PageDown: () => props.budget - BIG_STEP,
      Home: () => 0,
      End: () => props.pool,
    };
    if (event.key === " ") {
      event.preventDefault();
      if (!props.fixed) props.onToggleLock();
      return;
    }
    const move = moves[event.key];
    if (!move || props.locked) return;
    event.preventDefault();
    props.onChange(Math.max(0, move()));
  };

  return (
    <div className="split">
      <div className="split-head">
        <span className="name" id={labelId}>
          {props.name}
        </span>
        {props.expand && (
          <button
            type="button"
            className="expand-button"
            aria-expanded={props.expand.open}
            aria-controls={props.expand.controls}
            aria-label={props.expand.label}
            onClick={props.expand.onToggle}
          >
            <svg className="chevron" width="14" height="14" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
              <path d="M6 3.5 10.5 8 6 12.5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span aria-hidden="true">({props.expand.count})</span>
          </button>
        )}
        <AmountEntry {...props} shown={shown} />
        {props.onUnits && (
          <button
            type="button"
            role="switch"
            className="unit-switch"
            data-unit={props.units}
            aria-checked={props.units === "percent"}
            aria-label={t("budgets.unitSwitch", { name: props.name })}
            onClick={() => props.onUnits!(props.units === "percent" ? "dollars" : "percent")}
          >
            <span aria-hidden="true">$</span>
            <span className="unit-track" aria-hidden="true">
              <span className="unit-knob" />
            </span>
            <span aria-hidden="true">%</span>
          </button>
        )}
        <button
          type="button"
          className="lock-button"
          aria-pressed={props.locked}
          aria-label={t(props.locked ? "budgets.unlock" : "budgets.lock", { name: props.name })}
          disabled={props.fixed}
          title={props.fixed ? t("budgets.fixedLock") : undefined}
          onClick={props.onToggleLock}
        >
          <LockIcon locked={props.locked} />
          <span aria-hidden="true">{props.locked ? t(props.units === "percent" ? "budgets.lockedPercent" : "budgets.lockedDollars") : ""}</span>
        </button>
      </div>
      <div
        ref={track}
        className="split-track"
        role="slider"
        tabIndex={0}
        aria-label={t("budgets.sliderLabel", { name: props.name })}
        aria-describedby={footId}
        aria-valuemin={0}
        aria-valuemax={props.pool}
        aria-valuenow={props.budget}
        aria-valuetext={
          t("budgets.sliderValue", { amount: money(props.budget), percent: percent(share, 1), actual: money(props.actual) }) +
          (scheduled > 0 ? ` ${t("budgets.sliderScheduled", { amount: money(scheduled) })}` : "")
        }
        aria-disabled={props.locked || undefined}
        data-locked={props.locked}
        onKeyDown={onKeyDown}
        onPointerDown={(e) => {
          if (props.locked || e.button !== 0) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          e.currentTarget.focus();
          fromPointer(e);
        }}
        onPointerMove={(e) => {
          if (!props.locked && e.currentTarget.hasPointerCapture(e.pointerId)) fromPointer(e);
        }}
      >
        <div className="split-budget" style={{ width: width(props.budget) }} />
        <div className="split-actual" style={{ width: width(props.actual) }} />
        {scheduled > 0 && (
          <div
            className="split-scheduled"
            style={{ left: width(props.actual), width: `calc(${width(props.actual + scheduled)} - ${width(props.actual)})` }}
          />
        )}
        <div className="split-divider" />
        {!props.locked && <div className="split-thumb" style={{ left: width(props.budget) }} />}
      </div>
      <div className="split-foot" id={footId}>
        <span>
          {t("budgets.actual")}: {money(props.actual)}
          {scheduled > 0 && ` · ${t("budgets.scheduled")}: ${money(scheduled)}`}
        </span>
        <span className={remaining < 0 ? "negative" : undefined}>
          {remaining >= 0 ? t("budgets.left", { amount: money(remaining) }) : t("budgets.over", { amount: money(-remaining) })}
        </span>
      </div>
    </div>
  );
}

/**
 * The amount in the header. Clicking it (or Enter on it) turns it into a text
 * box for typing an exact amount, in the unit shown; Enter or leaving the box
 * applies it and Escape puts it back. A locked line just shows its amount.
 */
function AmountEntry(props: SplitSliderProps & { shown: string }) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(false);
  // After Enter or Escape, focus goes back to the amount rather than the top of the page.
  const refocus = useRef(false);
  const percent = props.units === "percent";
  if (props.locked || props.fixed)
    return (
      <span className="amount" aria-hidden="true">
        {props.shown}
      </span>
    );
  const close = (keepFocus: boolean) => {
    refocus.current = keepFocus;
    setDraft(null);
  };

  const start = () => {
    setInvalid(false);
    setDraft(percent ? toPercent(props.budget, props.pool).toFixed(2) : (props.budget / 100).toFixed(2));
  };
  const apply = (text: string): boolean => {
    const value = percent ? parsePercent(text) : parseMoney(text);
    if (value === null) return false;
    const amount = percent ? fromPercent(value, props.pool) : value;
    if (amount !== props.budget) props.onChange(amount);
    return true;
  };

  if (draft === null) {
    return (
      <button
        type="button"
        className="amount amount-button"
        ref={(el) => {
          if (el && refocus.current) {
            refocus.current = false;
            el.focus();
          }
        }}
        aria-label={t("budgets.typeAmount", { name: props.name, amount: props.shown })} onClick={start}>
        {props.shown}
      </button>
    );
  }
  return (
    <span className="amount amount-entry">
      {!percent && <span aria-hidden="true">$</span>}
      <input
        type="text"
        inputMode="decimal"
        autoFocus
        onFocus={(e) => e.currentTarget.select()}
        size={Math.max(4, draft.length + 1)}
        value={draft}
        aria-label={t(percent ? "budgets.typePercent" : "budgets.typeDollars", { name: props.name })}
        aria-invalid={invalid || undefined}
        onChange={(e) => {
          setDraft(e.target.value);
          setInvalid(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            if (apply(draft)) close(true);
            else setInvalid(true);
          } else if (e.key === "Escape") {
            e.preventDefault();
            close(true);
          }
        }}
        onBlur={() => {
          if (refocus.current) return;
          apply(draft);
          close(false);
        }}
      />
      {percent && <span aria-hidden="true">%</span>}
    </span>
  );
}

function LockIcon({ locked }: { locked: boolean }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" focusable="false">
      <rect x="3" y="7" width="10" height="7" rx="1.5" fill="currentColor" />
      <path d={locked ? "M5 7V5a3 3 0 0 1 6 0v2" : "M5 7V5a3 3 0 0 1 5.6-1.5"} stroke="currentColor" strokeWidth="1.6" fill="none" />
    </svg>
  );
}
